import { Box, createStyles } from '@mantine/core';
import MarkdownRenderer from '@repo/common/components/MarkdownRenderer';
import { Text } from '@repo/foundations';
import { get, isEmpty } from 'lodash-es';
import { useRef, useState } from 'react';
import type { ISecodaEntity } from '../../api/index.ts';
import { ROW_HEIGHT } from './TableV2.styles.ts';

interface OptimizedDescriptionRenderProps<T> {
	record: T;
	field: string;
	label?: string;
	disableHover?: boolean;
}

const useStyles = createStyles((theme) => ({
	wrapper: {
		height: `${ROW_HEIGHT - 16}px`,
		marginLeft: `-${theme.spacing.xs}`,
		borderRadius: theme.radius.sm,
		paddingLeft: theme.spacing.xs,
		paddingRight: theme.spacing.xs,
		display: 'flex',
		alignItems: 'center',
		// Necessary to hide the overflow of the markdown renderer.
		overflow: 'hidden',
		maxWidth: '100%',
	},
	wrapperWithHover: {
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/transparent/hover'),
		},
	},
	descriptionContainer: {
		lineHeight: '1.5',
		height: theme.other.space[7], // 28px
		width: '100%',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		display: 'flex',
		alignItems: 'center',
	},
}));

/**
 * Lightweight read-only component to display descriptions.
 * Used when user doesn't have permission to edit.
 */
export function OptimizedDescriptionRender<T extends ISecodaEntity>({
	record,
	field,
	label: _label,
	disableHover = false,
}: OptimizedDescriptionRenderProps<T>) {
	const { classes } = useStyles();
	const description = get(record, field);
	const [inputValue] = useState<string>(description ?? '');

	const descriptionRef = useRef<HTMLDivElement>(null);
	const wrapperRef = useRef<HTMLDivElement>(null);

	return (
		<Box
			ref={wrapperRef}
			className={
				disableHover
					? classes.wrapper
					: `${classes.wrapper} ${classes.wrapperWithHover}`
			}
		>
			<Box className={classes.descriptionContainer}>
				{!isEmpty(inputValue) && (
					<MarkdownRenderer
						ref={descriptionRef}
						ellipsis={true}
						inline
						lineClamp={1}
						fontSize="small"
					>
						{inputValue}
					</MarkdownRenderer>
				)}
				{isEmpty(inputValue) && (
					<Box aria-label="placeholder">
						<Text size="sm" color="text/secondary/default">
							{`Add ${_label}`}
						</Text>
					</Box>
				)}
			</Box>
		</Box>
	);
}
